import type { IsoTimestamp } from './common.js';
import type { AssetLabel } from './campaign.js';

// Stage 7A — Meta Ad Builder MVP (§2). A MetaAdDraft is the composition entity:
// a kyck-side Campaign → Ad Set → Ad triple that an AM composes with autosave,
// then publishes to Meta via Zernio. Owns the `metaAdDrafts` collection.
//
// SOURCE-OF-TRUTH SPLIT (§1.4): the DRAFT lives in kyck Firestore and is
// mutable while status==='draft'. The PUBLISHED ad lives in Meta — kyck stores
// only the Meta IDs + a frozen snapshot. Live status/metrics are fetched from
// Zernio on demand, never stored.
//
// LOCK-ON-PUBLISH (§1.5, decision Q4 = HOLD): once published, the composition
// triple is frozen into `publishedSnapshot`, the kyck record is read-only, and
// all further management (pause/budget/schedule) happens in Meta Ads Manager via
// the `fbAdAccountUrl` deep link. Edits require duplicate-to-new-draft. Mirrors
// surveys F2. (Meta DOES allow live edits via API; not exposing them is a
// deliberate product choice, revisitable post-MVP.)
//
// SCOPE DECISIONS (Phase A locked decisions, AMENDED 2026-06-02 after Zernio
// shipped five previously-absent createStandaloneAd capabilities). Status legend:
// IN = in MVP scope; DEFERRED = Zernio supports it, held to Phase 2; CUT = not
// shipping. All originally verified against Zernio's OpenAPI + SDK.
//   Q1  manual placements      — IN (RESTORED 2026-06-02). Zernio added a
//                                placements field to createStandaloneAd. Modelled
//                                here as AdSetConfig.placements (PlacementConfig).
//   Q2  schedule start         — IN (RESTORED 2026-06-02). createStandaloneAd now
//                                accepts startDate. AdSetConfig.startDate (null =
//                                live on publish) + endDate.
//   Q3  budget level (CBO/ABO) — IN (RESTORED 2026-06-02). Zernio added budget-
//                                level routing. CampaignConfig.budgetLevel selects
//                                CBO (CampaignConfig.budget) vs ABO (AdSetConfig.budget).
//   Q5  Instagram override     — DEFERRED to Phase 2 (capability exists upstream).
//                                Auto-derive from the Page stays the default + only
//                                MVP behaviour; the account-level IG override is an
//                                edge case. ad.instagram* remain display-only here.
//   --  per-placement creative — IN (2026-06-03). Zernio shipped placementAssets
//       (Story/Reels pinning)     (Meta asset_customization_rules). Modelled as
//                                creative.feedAsset + creative.storyAsset (D3.4 —
//                                each image XOR video; built into a placementAssets
//                                rule at the publish boundary).
//   --  dynamic creative        — DEFERRED to Phase 2. The auto-optimization
//       (auto-optimization pool)  creative POOL is a separate workflow, mutually
//                                exclusive with placementAssets; not in MVP.
//   --  callToAction           — aligned to Zernio's EXACT 18-value enum.
//   --  money                  — budget is amountCents (kyck integer-cents rule);
//                                converted to Zernio's whole-currency-units at the
//                                publish edge, in the AD ACCOUNT's currency.
//
// MONEY (kyck integer-cents rule): every budget is stored as integer minor units
// (cents) in `amountCents`. Zernio's `budgetAmount` is WHOLE currency units, so
// the publish boundary (Phase D) divides by 100. The currency is the Meta ad
// account's (from ads.listAdAccounts), NOT the kyck Account's — they can differ;
// the builder warns on mismatch and denominates the input in the ad account's.
// The EFFECTIVE budget is CampaignConfig.budget when budgetLevel==='campaign',
// else AdSetConfig.budget (budgetLevel==='adset'); exactly one is populated at
// publish (validated in A2.1).

export type MetaAdDraftStatus =
  | 'draft' // composing, mutable
  | 'publishing' // publish in flight, transient (CAS-guarded draft→publishing)
  | 'published' // success, frozen, Meta IDs populated
  | 'failed' // publish failed; record stays draft-like for edit/retry
  | 'cancelled'; // soft-cancelled by AM, preserved for audit

// Denormalised user reference (mirrors SurveyUserRef / Broadcast.deletedBy) so
// the list + detail render a name without a user lookup.
export type MetaAdUserRef = { uid: string; email: string; name: string };

// ---------- Campaign (builder section 1) ----------

// D2.17 — Meta campaign OBJECTIVE (ODAX). MVP exposes TRAFFIC + LEADS; AWARENESS /
// ENGAGEMENT / VIDEO_VIEWS / SALES are Phase 3. Replaces the legacy
// `goal: 'leads' | 'traffic'`. The objective + the ad set's optimizationGoal +
// destination together determine Zernio's flat `body.goal` at publish (see
// server/modules/metaAds/publishMapper.ts resolveDelivery).
export type CampaignObjective = 'TRAFFIC' | 'LEADS';

export type BudgetConfig = {
  amountCents: number; // integer minor units; ÷100 → Zernio budgetAmount at publish
  type: 'daily' | 'lifetime';
};

// Multi-ad-set: budgetMode replaces the old budgetLevel. CBO = a campaign-level
// budget (CampaignConfig.budget, Meta distributes across ad sets); ABO = a
// per-ad-set budget (AdSet.budget). budgetMode is LOCKED campaign-wide.
export type BudgetMode = 'CBO' | 'ABO';

// The delivery state the campaign + every ad set publish in. ACTIVE = live on
// publish (the historical default — no status field used to be sent and Meta
// defaults new objects to active); PAUSED = published but not delivering (the AM
// flips it on in Ads Manager when ready). Campaign-wide — one value drives every
// ad set's status at publish (see publishMapper). Bound to the builder's Publish
// Active / Publish Off toggle.
export type MetaAdPublishStatus = 'ACTIVE' | 'PAUSED';

// LEGACY publish-view value (the OLD single-ad-set budgetLevel). Used only by the
// publish path's legacy view (firstAdSetLegacyView) so the publish mapper stays
// byte-identical; never edited in the multi-ad-set UI. 'campaign' ≡ CBO, 'adset'
// ≡ ABO.
export type BudgetLevel = 'campaign' | 'adset';

// Campaign config (multi-ad-set). Owns every CAMPAIGN-WIDE-LOCKED field — the ones
// the spec keeps single across all ad sets so the optimization-goal mapping,
// lead-form/pixel handling, placement→creative routing and shared creative all
// stay computed ONCE: objective, budgetMode, the CBO budget, optimizationGoal,
// customEventType, destinationType, the Facebook Page, placements, and the default
// schedule (an ad set inherits these dates unless it overrides them).
export type CampaignConfig = {
  objective: CampaignObjective; // D2.17 (was `goal`)
  budgetMode: BudgetMode; // CBO (campaign budget) | ABO (per-ad-set budget)
  budget: BudgetConfig | null; // CBO budget; populated only when budgetMode==='CBO'
  publishStatus: MetaAdPublishStatus; // ACTIVE (default) | PAUSED — the state ad sets publish in

  // LOCKED campaign-wide (moved off the per-ad-set config — shared by every ad set).
  optimizationGoal: string; // D2.17 — what Meta optimizes delivery for
  customEventType: string | null; // required iff optimizationGoal==='OFFSITE_CONVERSIONS'
  destinationType: 'instant_form' | 'website'; // drives the shared creative's destination
  facebookPageId: string | null; // the Page connected via the client's Zernio profile
  facebookPageName: string | null; // denormalised for display
  placements: PlacementConfig; // Q1 placement targeting (automatic by default)

  // Default schedule. An AdSet uses these unless AdSet.datesOverridden is true.
  // Stored as a WALL-CLOCK local datetime 'YYYY-MM-DDTHH:mm' (no timezone) — the
  // time the AM typed. Resolved to an absolute instant against the effective tz
  // (account tz, or Australian Eastern when scheduleUseAustralianEastern) at the
  // publish + validation boundaries (src/lib/scheduleTime.ts). null startDate =
  // live on publish; endDate optional (lifetime budgets need one). Default time-of-
  // day when a date is picked: 5:00am start / 10:00pm end (editable).
  defaultStartDate: IsoTimestamp | null;
  defaultEndDate: IsoTimestamp | null;
  // Timezone override (B): false (default) = resolve schedule wall-clock against the
  // AD ACCOUNT's timezone (correct — Meta runs the campaign in account tz). true =
  // resolve against Australian Eastern (AEST/AEDT, DST-correct) — for an ad account
  // misconfigured to the wrong timezone where we want AU-eastern scheduling.
  scheduleUseAustralianEastern: boolean;
};

// LEGACY publish-view campaign config (the OLD CampaignConfig shape). Built by
// firstAdSetLegacyView from the new campaign so the publish mapper's
// resolveBudget/buildCreateAdRequest read it unchanged. NOT stored.
export type LegacyCampaignConfig = {
  objective: CampaignObjective;
  budgetLevel: BudgetLevel;
  budget: BudgetConfig | null;
  publishStatus: MetaAdPublishStatus; // carried through so the mapper sets body.status
};

// ---------- Ad Set (builder section 2) ----------

export type TargetingCity = {
  key: string; // Meta city key from ads.searchAdTargeting (dimension=geo, geoType=city)
  name: string; // denormalised for display
  radius: number;
  distanceUnit: 'kilometer' | 'mile';
  // The Meta search-result breadcrumb captured on selection, e.g.
  // ['Australia','Queensland','Parkwood'] (path[0] = the parent country label).
  // Two jobs: (1) disambiguates same-named cities in the picker / chip, and
  // (2) is the SOURCE for the parent country Meta REQUIRES alongside city
  // targeting — a city with no country yields an empty reach estimate and a
  // blocked publish. Optional: legacy/extracted cities created before this field
  // omit it (derivation falls back to null → validation flags it pre-publish).
  path?: string[] | null;
  // Resolved ISO-3166-1 alpha-2 parent country (from path[0], or the countryCode
  // the city search was scoped by). null when unresolvable. Optional for the same
  // legacy reason as `path`.
  countryCode?: string | null;
};

export type TargetingRegion = {
  key: string; // Meta region key; Zernio's create takes key only
  name: string; // denormalised for display (not sent)
  // Audience-builder (drawn boundaries) — the search-result breadcrumb + resolved
  // parent country, so the region's centre/placeId geocodes unambiguously
  // ("New South Wales, Australia"). Optional: legacy/extracted regions omit them.
  path?: string[] | null;
  countryCode?: string | null;
};

// Audience-builder — a POSTCODE/ZIP target (Meta geo_locations.zips). Key-based AREA
// geo (geoType=zip) with NO radius — drawn as a boundary polygon (AU postcodes via ABS
// POA GeoJSON), not a point. `name` is the postcode string (e.g. "4217"), the join key
// to the ABS dataset. Additive; sub-country geo (suppresses the country).
export type TargetingZip = {
  key: string; // Meta zip key from geo search (geoType=zip)
  name: string; // the postcode string — the ABS POA join key
  path?: string[] | null;
  countryCode?: string | null;
};

// Audience-builder — a PLACE target (Meta geo_locations.places). Key-based geo like
// a city (resolved via Zernio geo search, geoType=place), WITH a radius — drawn as a
// draggable circle. Radius band is the PLACE band (1–80 km, same as custom locations,
// NOT the city 17 km min). `path`/`countryCode` mirror TargetingCity (disambiguation +
// geocoding). Additive (coexists with cities); sub-country geo (suppresses country).
export type TargetingPlace = {
  key: string; // Meta place key from geo search (geoType=place)
  name: string;
  radius: number; // in distanceUnit; clamped to the PLACE band (1–80 km)
  distanceUnit: 'kilometer' | 'mile';
  path?: string[] | null;
  countryCode?: string | null;
};

// Audience-builder — a NEIGHBORHOOD target (Meta geo_locations.neighborhoods).
// Key-based geo (geoType=neighborhood) with NO radius — Meta shows it as a pin, so
// it's drawn as a marker only (no circle, not resizable). Additive; sub-country geo.
export type TargetingNeighborhood = {
  key: string; // Meta neighborhood key from geo search (geoType=neighborhood)
  name: string;
  path?: string[] | null;
  countryCode?: string | null;
};

// Audience-builder Phase 1 — a point-radius geo target (Meta
// geo_locations.custom_locations / Zernio camelCase `customLocations`). Dropped on
// the map (lat/long) with a draggable radius. ADDITIVE: coexists with cities and is
// NOT part of the mutually-exclusive geoLevel system (a pin sits alongside the
// city/region/country layer). Like cities, a present custom location is sub-country
// geo, so it suppresses any country-level target at publish/reach (see
// metaCountries). radius is in `distanceUnit` (NOT meters — the map converts at the
// google.maps.Circle boundary). `name` is an optional human label for the chip.
export type TargetingCustomLocation = {
  latitude: number; // -90..90
  longitude: number; // -180..180
  radius: number; // in distanceUnit; clamped to CUSTOM_LOCATION_RADIUS_BOUNDS at the edges
  distanceUnit: 'kilometer' | 'mile';
  name?: string | null; // optional label (reverse-geocode/AM-typed); display-only
};

export type TargetingInterest = {
  id: string; // Meta interest id from ads.searchAdInterests
  name: string;
};

// How this targeting was populated. Becomes 'manual' once any field is edited
// after applying a preset/extraction (§3.4).
export type TargetingSourceType = 'manual' | 'extracted' | 'preset';

// D1-followup: geographic targeting is MUTUALLY EXCLUSIVE — an AM picks ONE
// level. Exactly the matching layer is populated; the others are empty. (ZIP is
// Phase 2, deliberately absent from this enum.) Manual mode only — extracted
// (locked) targeting bypasses the constraint and keeps its raw Meta shape.
export type GeoLevel = 'country' | 'region' | 'city';

// D2.15 — Meta geo_locations.location_types: who in the geo area sees the ad.
//   'home'        → people who LIVE in the area (recommended for franchise local).
//   'home_recent' → live OR recently in the area (Meta's default, broadest).
//   'recent'      → people recently in the area (travel/tourism).
export type LocationTypes = 'home' | 'home_recent' | 'recent';

// Audience-builder — the EXCLUDED geo bucket (Meta excluded_geo_locations). Mirrors
// the included geo structure (same item types) for every sub-country geo type. A
// location is INCLUDED (the flat fields below) OR EXCLUDED (here) — the AM picks via
// the include/exclude toggle, or flips an item between buckets. NO countries (you can't
// exclude a country within a country-targeted ad). Legacy → all empty on migrate.
export type ExcludedGeo = {
  regions: TargetingRegion[];
  cities: TargetingCity[];
  places: TargetingPlace[];
  neighborhoods: TargetingNeighborhood[];
  zips: TargetingZip[];
  customLocations: TargetingCustomLocation[];
};

export const EMPTY_EXCLUDED_GEO: ExcludedGeo = {
  regions: [],
  cities: [],
  places: [],
  neighborhoods: [],
  zips: [],
  customLocations: [],
};

export type TargetingConfig = {
  // Location — mutually exclusive per geoLevel (manual mode).
  geoLevel: GeoLevel | null; // null = legacy (pre-geoLevel); derived on read
  countries: string[]; // ISO-3166-1 alpha-2 (default ['AU'])
  cities: TargetingCity[];
  regions: TargetingRegion[];
  // Audience-builder — places (circle + radius) and neighborhoods (pin only). ADDITIVE
  // (coexist with the selected geoLevel layer; searched via geoType=place/neighborhood).
  // Legacy drafts predate these → migrateTargetingShape defaults them to [].
  places: TargetingPlace[];
  neighborhoods: TargetingNeighborhood[];
  // Audience-builder (drawn boundaries) — postcodes/zips (AREA, drawn as boundary
  // polygons). ADDITIVE; searched via geoType=zip. Legacy → [] on migrate.
  zips: TargetingZip[];
  // Audience-builder Phase 1 — point-radius pins (map-dropped). ADDITIVE: coexist
  // with the selected geoLevel layer (a pin doesn't disturb cities). Legacy drafts
  // predate this → migrateTargetingShape defaults it to []. Manual mode only;
  // extracted targeting carries its custom_locations inside rawPayload verbatim.
  customLocations: TargetingCustomLocation[];
  // Audience-builder — manual EXCLUSIONS (Meta excluded_geo_locations), mirroring the
  // included geo above. Legacy → EMPTY_EXCLUDED_GEO on migrate.
  excluded: ExcludedGeo;
  // D2.15 — home vs recent (manual mode). Default 'home_recent' (Meta default).
  // Extracted mode ignores this — location_types comes from rawPayload.
  locationTypes: LocationTypes;

  // Demographics
  ageMin: number; // 13–65
  ageMax: number; // 13–65
  gender: 'all' | 'male' | 'female';

  // Interests
  interests: TargetingInterest[];

  // Provenance
  sourceType: TargetingSourceType;
  sourcePresetId: string | null; // FK to MetaTargetingPreset when sourceType==='preset'
  sourceCampaignId: string | null; // Meta campaign id when sourceType==='extracted'
  sourceCampaignName: string | null; // human-readable label for the extracted-from banner (survives reload)
  // C5-polish: the specific ad set the targeting was extracted from (campaigns can
  // hold multiple ad sets with deliberately different audiences for A/B). Both null
  // for manual or single-ad-set extraction with no chosen ad set.
  sourceAdSetId: string | null;
  sourceAdSetName: string | null;
  // C5-revive: when sourceType==='extracted', the VERBATIM targeting payload from
  // the source ad — Meta-native snake_case (external ads) or Zernio camelCase
  // (Zernio-created), preserved as-is for publish pass-through + advanced-field
  // detection. null for manual. sourceType==='extracted' is the "locked" signal:
  // the kyck fields above are NOT edited in this mode and validation skips them.
  rawPayload: unknown | null;
};

// ---------- placements (D3.1 — full placement matrix) ----------
//
// Meta's publisher_platforms + per-platform position arrays, which Zernio's
// createStandaloneAd accepts. D3.1 expands to all four platforms (facebook /
// instagram / messenger / audience_network) with per-position control + Feed/Story
// asset routing (see src/lib/placementMatrix.ts for the catalogue, asset classes,
// defaults, and dependencies). Positions are stored as `string[]` (not a typed
// enum): the matrix includes a few values Zernio's SDK type doesn't enumerate yet
// (biz_disco_feed, audience-network native/instream_video) — these ride the loose
// publish body and are operator-verified.

export type PublisherPlatform = 'facebook' | 'instagram' | 'messenger' | 'audience_network';

export type ManualPlacementConfig = {
  publisherPlatforms: PublisherPlatform[];
  facebookPositions: string[];
  instagramPositions: string[];
  messengerPositions: string[];
  audienceNetworkPositions: string[];
};

export type PlacementConfig = {
  mode: 'automatic' | 'manual';
  manualConfig: ManualPlacementConfig | null; // populated only when mode==='manual'
};

// LEGACY publish-view ad-set config (the OLD single-ad-set shape). The
// campaign-wide-locked fields (optimizationGoal / customEventType /
// destinationType / facebookPage* / placements) now live on CampaignConfig and
// the per-ad-set fields on AdSet; this flattened shape is reconstructed ONLY by
// firstAdSetLegacyView so the publish mapper reads it unchanged. NOT stored.
export type AdSetConfig = {
  // D2.14 — optional ad-set name (Meta hierarchy). '' falls back to draft.title
  // (the campaign name) at publish → body.adSetName. Legacy drafts read as ''.
  name: string;

  // D2.17 — what Meta optimizes delivery for. Valid values depend on the campaign
  // objective + destinationType (see metaAdObjectiveShape.validOptimizationGoals):
  //   TRAFFIC: LINK_CLICKS | LANDING_PAGE_VIEWS | REACH | IMPRESSIONS
  //   LEADS + instant_form: LEAD_GENERATION (locked)
  //   LEADS + website: OFFSITE_CONVERSIONS | LANDING_PAGE_VIEWS | LINK_CLICKS
  optimizationGoal: string;
  // The pixel event Meta optimizes for — REQUIRED when optimizationGoal ===
  // 'OFFSITE_CONVERSIONS', null otherwise (e.g. 'LEAD', 'COMPLETE_REGISTRATION').
  customEventType: string | null;

  // Instant Form valid only when objective==='LEADS'; Website valid for either.
  destinationType: 'instant_form' | 'website';

  // Page selected from those connected to the ad account (connect.getFacebookPages).
  facebookPageId: string | null;
  facebookPageName: string | null; // denormalised for display

  // Q2 (restored): startDate null = live on publish; if set, must be future.
  // endDate optional (Zernio REQUIRES it for lifetime budgets; A2 enforces).
  startDate: IsoTimestamp | null;
  endDate: IsoTimestamp | null;

  targeting: TargetingConfig;

  // Q1 (restored): placement targeting. Defaults to { mode: 'automatic' }.
  placements: PlacementConfig;

  // Q3 (restored): ABO budget — populated only when CampaignConfig.budgetLevel
  // === 'adset'. Null under CBO (budget lives on CampaignConfig.budget).
  budget: BudgetConfig | null;

  // Per-ad-set publish status (carried through so the mapper sends THIS ad set's
  // own status). The campaign gates: a PAUSED campaign forces every ad set paused.
  status: MetaAdPublishStatus;
};

// ---------- Ad Set (multi-ad-set, builder section 2) ----------
//
// Per-ad-set publish checkpoint (SERVER-OWNED — populated by the Part-2 publish
// orchestration, which fans out one create per ad set under the shared campaign +
// creative). Defined now so the schema/migration/merge plumbing is ready; Part 1
// never writes it (always null). Mirrors AdditionalAdPublishState: a stale client
// PATCH can't clobber it (the service re-merges by ad-set id from the stored doc).
export type AdSetPublishState = {
  status: 'queued' | 'creating' | 'attaching' | 'complete' | 'failed';
  platformCampaignId: string | null;
  platformAdSetId: string | null;
  platformAdIds: string[]; // the ad(s) created under this ad set (flat projection of attachedAds)
  // Part 2b — per-shared-ad attach record for THIS ad set (resumable multi-ad
  // fan-out). adKey is 'primary' for the shared primary ad, else the AdditionalAd.id.
  // Every ad set carries ALL the campaign's shared ads; this lets a retry attach only
  // the shared ads still missing from this ad set (no duplicate ads). A fan-out ad
  // set is 'complete' only once every shared ad is attached.
  attachedAds: AdSetAttachedAd[];
  error: string | null;
  publishedAt: IsoTimestamp | null;
};

// One shared ad attached to a given ad set (Part 2b). platformAdId null = attempted
// but not yet live (the ad set then stays incomplete until a retry attaches it).
export type AdSetAttachedAd = {
  adKey: string; // 'primary' for the shared primary ad; else the AdditionalAd.id
  platformAdId: string | null;
};

// One ad set in a campaign. A normal campaign is a single AdSet. What VARIES per
// ad set (Phase 1): targeting (ALWAYS — the audience/location being tested),
// budget (ABO only), and the schedule (inherits the campaign default unless
// datesOverridden). Everything else (objective, optimization, placements, the
// shared creative) is campaign-wide-locked. `id` is a stable local draft id
// (a uuid for UI-created sets, a stable derived id for migrated/legacy ones) used
// for React keys, autosave merge, and per-ad-set validation/publishState matching.
export type AdSet = {
  id: string;
  name: string;
  targeting: TargetingConfig; // always its own
  budget: BudgetConfig | null; // ABO only (CampaignConfig.budgetMode==='ABO'); null under CBO
  datesOverridden: boolean; // false (default) = inherit CampaignConfig.defaultStart/End
  // WALL-CLOCK local datetime 'YYYY-MM-DDTHH:mm' (no tz), resolved against the same
  // effective tz as the campaign default (see CampaignConfig.defaultStartDate).
  startDate: IsoTimestamp | null; // meaningful only when datesOverridden
  endDate: IsoTimestamp | null; // meaningful only when datesOverridden
  // Per-ad-set Active/Off (default ACTIVE). INDEPENDENT of CampaignConfig.publishStatus
  // (Meta models them as separate fields): an AM can publish several audiences with
  // only one ACTIVE and the rest PAUSED-and-ready (swap if the active one
  // underperforms), under a campaign in any state. A PAUSED campaign prevents
  // delivery but does NOT force this value (see resolveAdSetPublishStatus in
  // src/lib/metaAdPublishStatus.ts). Every ad on the ad set inherits this status.
  status: MetaAdPublishStatus;
  publishState: AdSetPublishState | null; // server-owned (Part 2); null in Part 1
};

// ---------- Ad (builder section 3) ----------

// Zernio's EXACT callToAction enum (createStandaloneAd). Do NOT add Meta values
// Zernio doesn't accept (the original spec's BOOK_NOW / APPLY_NOW / CALL_NOW /
// SEND_MESSAGE are absent and would be rejected).
export type MetaCallToAction =
  | 'LEARN_MORE'
  | 'SHOP_NOW'
  | 'SIGN_UP'
  | 'BOOK_TRAVEL'
  | 'CONTACT_US'
  | 'DOWNLOAD'
  | 'GET_OFFER'
  | 'GET_QUOTE'
  | 'SUBSCRIBE'
  | 'WATCH_MORE'
  | 'REGISTER'
  | 'JOIN'
  | 'ATTEND'
  | 'REQUEST_DEMO'
  | 'VIEW_QUOTE'
  | 'APPLY'
  | 'SEE_MORE'
  | 'BUY_NOW';

export type AdDestination =
  | { type: 'instant_form'; leadGenFormId: string; leadGenFormName: string }
  | { type: 'website'; url: string };

// A single creative asset slot (D3.4). Image XOR video; `type` is null when the
// slot is empty. `url` is the Firebase Storage signed READ URL Zernio fetches at
// publish; `path` is the durable Storage object handle (kept for lifecycle /
// future re-sign — inert at publish, which uses `url`). `thumbnailUrl` is video-
// only and OPTIONAL — Meta auto-generates a first-frame thumbnail when omitted.
export type CreativeAsset = {
  type: 'image' | 'video' | null;
  url: string | null;
  path: string | null; // Firebase Storage path (durable handle)
  thumbnailUrl: string | null; // video only; null → Meta auto-generates
  thumbnailPath: string | null; // video only
  // Clone (Part B): media REUSED from a live ad in the same ad account. An image carries
  // Meta's image hash, a video its video id; url / path stay null. Sent as imageHash /
  // video.id, never as a URL. Absent on every uploaded asset.
  imageHash?: string | null;
  videoId?: string | null;
};

// The ad creative. Two asset slots (D3.4): `feedAsset` drives Feed-class
// placements; `storyAsset` (optional) drives Story-class placements via Meta's
// asset_customization_rules (`placementAssets`). Both slots are image XOR video,
// and the UI enforces a SINGLE type across both (`assetType`) — Zernio/Meta can't
// mix image + video in one placementAssets block (Miki 2026-06-08). A populated
// storyAsset + Story-class placements build a placementAssets rule pinning the
// Story asset; an empty storyAsset = a plain single-asset ad.
export type AdCreative = {
  // Dynamic Creative toggle (Phase 1): false (default) = a standard single-text
  // ad — one headline + one primary text, published in the single-creative shape.
  // true = Multiple Text Options — the multi-input fields unlock (up to 5 each)
  // and publish goes through Meta's dynamicCreative shape (D3.3c), EVEN when only
  // 1+1 is filled in (Meta accepts a single-variation DCO ad set). Legacy drafts
  // that predate the flag derive it on read from the array lengths — see
  // migrateCreativeShape.
  useDynamicCreative: boolean;

  // Multi-input (C7-extension): when useDynamicCreative, Meta accepts up to 5 of
  // each and optimises. headlines/primaryTexts carry 1–5 items, descriptions 0–4.
  // Legacy single-value drafts (headline/primaryText/description strings) are
  // migrated to single-item arrays on read — see migrateCreativeShape.
  headlines: string[]; // 1–5, each ≤40 (Meta soft cap); single item unless useDynamicCreative
  primaryTexts: string[]; // 1–5, each ≤125 recommended / ≤900 hard; single item unless useDynamicCreative
  descriptions: string[]; // 0–4, each ≤30
  callToAction: MetaCallToAction;

  // The single asset type across both slots (D3.4). The UI's one selector; each
  // populated slot's `.type` matches this. Defaults 'image'.
  assetType: 'image' | 'video';
  feedAsset: CreativeAsset; // Feed-class placements (defaultImageUrl/defaultVideoUrl)
  storyAsset: CreativeAsset; // Story-class placements (placementAssets rule); empty = none
};

// ---------- Phase 2 — multiple ads per ad set (sequential attach) ----------

// Per-ad copy overrides for an additional ad. null on a field = inherit the
// shared value from the primary ad's creative (headlines[0] / primaryTexts[0] /
// descriptions[0] / callToAction / destination URL).
export type AdCopyOverride = {
  headline: string | null;
  primaryText: string | null;
  description: string | null;
  callToAction: MetaCallToAction | null;
  linkUrl: string | null;
};

// Per-ad publish state for partial-failure retry. SERVER-OWNED: stamped by the
// publish service after each attach call; the update path re-merges it from the
// stored doc by entry id, so a stale client PATCH can never clobber it. null =
// not yet attempted. Stored as a concrete ISO string (not a Firestore sentinel —
// serverTimestamp() is illegal inside array elements).
export type AdditionalAdPublishState = {
  status: 'published' | 'failed';
  platformAdId: string | null;
  // Part 2b — the shared creative Meta built for this ad when it was first created on
  // the primary ad set. Reused (existingCreativeId) to attach this ad to the
  // ADDITIONAL ad sets without re-uploading its assets — so one creative is shared
  // across every ad set, not one per ad-set. null when the ad never published.
  platformCreativeId: string | null;
  error: string | null; // Meta/Zernio error message when failed
  publishedAt: IsoTimestamp | null;
};

// An additional ad attached to the primary ad's ad set (Zernio attach shape:
// adSetId + a full single-creative body). Budget / targeting / goal / schedule
// inherit from the ad set; each ad carries its own Feed asset, optional Story
// asset (placementAssets) and optional copy overrides.
//
// Phase 2a: each entry has its OWN assetType (image | video), fully independent
// per ad — a mixed-media set (image ads + video ads under one ad set) is valid;
// vendor-confirmed video attach works. Within ONE ad both slots share the type
// (Meta: a placementAssets block is all-image or all-video). Video slots need an
// explicit thumbnail — the attach path has no auto-extraction (D3.4b pending).
export type AdditionalAd = {
  id: string; // client uuid (React keys + autosave merge)
  name: string; // ad display name
  assetType: 'image' | 'video'; // both slots share it; legacy entries migrate to 'image'
  feedAsset: CreativeAsset; // required; thumbnail REQUIRED when video
  storyAsset: CreativeAsset; // optional; empty slot = no Story routing
  copyOverride: AdCopyOverride | null; // null = inherit all shared copy
  // Per-ad Active/Off (default ACTIVE). INDEPENDENT of the ad set + campaign status
  // (Meta's three-level model: each level holds its own status; a paused parent stops
  // delivery but does NOT force the child's status). This ad's OWN status flows to its
  // attach body verbatim. Legacy/migrated ads default ACTIVE.
  status: MetaAdPublishStatus;
  publishState: AdditionalAdPublishState | null;
};

// Captured from the primary ad's publish response (Phase 2). Enables idempotent
// retry: a re-publish of a partially-published draft attaches ONLY the
// unpublished/failed ads to the stored ad set instead of creating a new campaign.
export type PublishedAdSetRef = {
  platformCampaignId: string;
  platformAdSetId: string;
};

export type UtmParams = {
  source: string; // utm_source (default 'facebook')
  medium: string; // utm_medium (default 'paid_social')
  campaign: string; // utm_campaign
  content: string; // utm_content
  term: string; // utm_term (optional)
};

export type AdConfig = {
  // D2.14 — optional ad name (Meta hierarchy). '' falls back to draft.title (the
  // campaign name) at publish → body.adName. Legacy drafts read as ''.
  name: string;

  // Q5 (wired D2.7): the IG Business Account the ad delivers as. When set, the
  // publish mapper sends it as the top-level `instagramAccountId` (Zernio maps it
  // to the creative's instagram_user_id); when null Meta auto-derives the IG
  // account from the connected Page (the default). instagramAccountName is
  // denormalised display context only (not sent).
  instagramAccountId: string | null;
  instagramAccountName: string | null;

  format: 'single_image' | 'single_video';

  destination: AdDestination;
  creative: AdCreative; // the Feed + Story asset slots (D3.4)

  // Conversion Pixel. Attached at the AD LEVEL via body.tracking.pixelId (D2.10c)
  // on every published ad for MEASUREMENT — events show in Ads Manager reporting —
  // but does NOT drive optimization (goal stays leads/traffic; the 'conversions'
  // goal + promotedObject.pixelId + customEventType is Phase 2). NOT sent in
  // promotedObject (Meta rejects pixelId there for non-conversions goals). (§3.5)
  pixelId: string | null;
  utmParams: UtmParams;

  // Per-ad Active/Off (default ACTIVE) for the PRIMARY ad. INDEPENDENT of the ad set +
  // campaign status (Meta's three-level model — a paused parent stops delivery but
  // does NOT force this value). This ad's OWN status flows to its create body verbatim.
  // Legacy/migrated drafts default ACTIVE.
  status: MetaAdPublishStatus;

  // Phase 2 — additional ads in the same ad set (sequential attach). [] = the
  // single-ad draft (existing behaviour, byte-identical publish). Mutually
  // exclusive with creative.useDynamicCreative (Meta: a DCO ad set takes exactly
  // one ad). Cap: 10 ads total (1 primary + 9 additional).
  additionalAds: AdditionalAd[];
};

// ---------- frozen snapshot + publish error ----------

export type PublishedMetaAdSnapshot = {
  campaign: CampaignConfig;
  adSets: AdSet[];
  ads: AdConfig;
  publishedFromVersion: number; // the draft `version` at publish time
};

// The publish path's LEGACY VIEW of a draft (the OLD single-ad-set
// {campaign, adSet, ad} triple). firstAdSetLegacyView projects the new
// adSets[0] + the campaign-wide-locked fields + the shared `ads` into this shape
// so publishMapper/publishService read it byte-identically. Part 2 replaces the
// single-ad-set projection with the per-ad-set fan-out. NOT stored.
export type LegacyPublishDraft = {
  id: string;
  title: string;
  campaign: LegacyCampaignConfig;
  adSet: AdSetConfig;
  ad: AdConfig;
};

export type PublishErrorCategory =
  | 'auth' // Zernio token expired / ad account not connected
  | 'validation' // composition fails Meta's validation
  | 'permission' // ad account lacks required permissions
  | 'page_not_paired' // Page not paired with the required resource
  | 'asset_upload' // creative asset upload/transcode failed
  | 'rate_limit' // Meta or Zernio rate limit
  | 'platform_error' // generic Meta API error
  | 'network' // request never reached Zernio
  // createStandaloneAd-specific refinements (D2.3) — narrowed from a Zernio
  // validation / generic-rejection by inspecting the error message + fields, so
  // the AM gets an actionable reason AND we capture the empirical-test signal.
  | 'verified_advertiser_required' // Meta subcode 3858634: AU ads need a verified advertiser ID
  | 'targeting_rejected' // Meta/Zernio rejected the audience/targeting shape (Q3 empirical unknown #1)
  | 'creative_rejected' // Meta/Zernio rejected the creative shape (Q1 empirical unknown #2)
  | 'invalid_input_generic' // Zernio's bare "Invalid input" with no field detail (the Miki case)
  | 'meta_policy_rejected' // Meta disapproved the ad on policy grounds
  | 'unknown';

export type PublishError = {
  occurredAt: IsoTimestamp;
  category: PublishErrorCategory;
  message: string; // human-readable, never a raw stack/path
  zernioErrorCode: string | null;
  metaSubcode: number | null; // e.g. 1815089 (Lead Gen TOS not accepted)
  retryable: boolean;
  // Seconds until a rate-limited request may be retried (RateLimitError
  // .getSecondsUntilReset()); null when not rate-limited / unknown. Added B1b.
  retryAfterSeconds: number | null;
};

// ---------- Build Campaign Phase 2 (Drive → creative) ----------
//
// Snapshot of the rollout row's Drive folders + asset labels, captured by the
// Build Campaign (Phase 1) assembly onto the draft so the builder's "Pull creative
// from Drive" action (Phase 2) is self-contained — it never reaches back into the
// rollouts module. Folders are listed LIVE at pull time (so new files appear);
// the LABELS are the snapshot (the authoritative feed/story AssetLabel.adType,
// keyed by driveFileId). Re-run Build Campaign to refresh the snapshot. null on
// drafts not created from a rollout (the normal manual-compose path).
export type MetaAdDriveSource = {
  rolloutId: string;
  rowId: string;
  // The three rollout folders (raw Drive URLs; may be null when unset). Pooled,
  // National → Local → Organic, for the 10-ad cap drop order.
  folders: {
    national: string | null; // metaAssetsFolderUrl
    local: string | null; // localAssetsUrl
    organic: string | null; // organicAssetsUrl
  };
  // The per-folder authoritative labels (AssetLabel.adType is the feed/story
  // determinant; the free-text `label` is ignored by Phase 2).
  labels: {
    national: AssetLabel[]; // metaAssetLabels
    local: AssetLabel[]; // localAssetLabels
    organic: AssetLabel[]; // organicAssetLabels
  };
};

// ---------- "Add an ad to a live ad set" target ----------

export type AttachDestination = 'website' | 'instant_form' | 'unknown';

export type AttachTarget = {
  campaignId: string; // Meta platform campaign id
  campaignName: string;
  adSetId: string; // Meta platform ad set id, the ONLY thing the publish sends to place the ad
  adSetName: string;
  adSetStatus: string | null;
  // INFERRED from the ad set's optimisation goal (Zernio does not return it).
  destination: AttachDestination;
  // The ad the new one was started from (null = started blank); display only.
  sourceAdId: string | null;
  sourceAdName: string | null;
  // Identity taken from the source ad (locked in the composer).
  pageId: string | null;
  instagramUserId: string | null;
  // ---- Part A (true duplicate) ----
  // Read from the source ad at pick time. All of these are LOCKED with the rest of the
  // target (restored from the stored draft on update) except `mediaMode`, the AM's choice.
  sourceCampaignId?: string | null;
  sourceCreativeId?: string | null;
  sourceImageHash?: string | null;
  sourceVideoId?: string | null;
  sourceThumbnailUrl?: string | null;
  // 'new' (default) = upload new media; 'original' = reuse the source image hash / video id
  // (copy stays editable); 'exact' = attach the source creative unchanged (existingCreativeId).
  mediaMode?: AttachMediaMode;
};

export type AttachMediaMode = 'new' | 'original' | 'exact';

export type ClonedFrom = { campaignId: string; campaignName: string };

// ---------- the MetaAdDraft doc ----------

export type MetaAdDraft = {
  id: string;

  // Metadata
  title: string;
  description: string | null; // internal notes
  status: MetaAdDraftStatus;

  // Optimistic-concurrency version. Bumped on every draft update; captured into
  // publishedSnapshot.publishedFromVersion. The CAS publish guard transitions
  // status draft→publishing transactionally (concurrent-publish protection).
  version: number;

  // Target kyck Account (must have a non-empty `fbAdAccountId` = Meta act_…).
  accountId: string;

  // Composition (mutable while status==='draft'; frozen via publishedSnapshot).
  // Multi-ad-set: `adSets` (>= 1; a normal campaign is one) each carry their own
  // targeting/budget/dates; `ads` is the SHARED creative applied to every ad set
  // (per-ad-set creative override is Phase 2). The campaign-wide-locked config
  // lives on `campaign`.
  campaign: CampaignConfig;
  adSets: AdSet[];
  ads: AdConfig;

  // Build Campaign Phase 2: the rollout Drive source captured at Phase-1 assembly
  // (null on manually-composed drafts). Drives the in-builder "Pull creative from
  // Drive" action. Round-tripped on every patch (mutable while draft).
  driveSource: MetaAdDriveSource | null;

  // "Add an ad to a live ad set": when set, this draft ONLY attaches one new ad to an
  // existing, live ad set. The campaign / adSets above are inert placeholders (never
  // sent); only `ads` is published, through the attach path. Set at creation and
  // LOCKED: the server ignores any change to it on update. Absent/null on every
  // ordinary draft. (See server/modules/metaAds/liveAdSetPublish.ts.)
  attachTarget: AttachTarget | null;
  // Clone to a new campaign (Part B): the live campaign this draft was cloned from.
  // LOCKED after creation. Its presence also forces the FIRST publish to PAUSED,
  // server-side, whatever the draft's statuses say.
  clonedFrom?: ClonedFrom | null;

  // Published state (populated on success; the kyck record is read-only after)
  publishedSnapshot: PublishedMetaAdSnapshot | null;
  // Phase 2 — the Meta campaign + ad set created by the PRIMARY ad's publish,
  // persisted immediately (before any attach call, crash-safe). Server-owned.
  // Non-null on a 'draft'-status doc = a partially-published multi-ad draft;
  // re-publish attaches only the unpublished/failed additional ads to this ad
  // set instead of creating a new campaign.
  publishedAdSet: PublishedAdSetRef | null;
  publishedMetaCampaignId: string | null;
  // Multi-ad-set Phase 1 (Part 2) — the Meta creative id created by the PRIMARY
  // ad set's publish (Miki's `ad.creative.creativeId`). Server-owned, captured on
  // the first create and REUSED via `existingCreativeId` when fanning the shared
  // creative across ad sets 1..n (no re-upload) — and on a resumed/retried publish
  // so additional ad sets attach to the same creative, never a duplicate.
  publishedMetaCreativeId: string | null;
  publishedMetaAdSetId: string | null;
  publishedMetaAdId: string | null;
  publishedAt: IsoTimestamp | null;
  publishedBy: MetaAdUserRef | null;
  publishError: PublishError | null; // populated when status==='failed'

  // Lifecycle + audit
  createdAt: IsoTimestamp;
  createdBy: MetaAdUserRef;
  updatedAt: IsoTimestamp;
  // Denormalised "last edited by" (E1) — stamped on every mutation (create =
  // creator; edit/publish/archive = the acting user). null on legacy drafts that
  // predate the field; the UI falls back to createdBy.
  updatedBy: MetaAdUserRef | null;
  deletedAt: IsoTimestamp | null; // soft-delete = "archived" (broadcast v6 / survey F pattern)
  deletedBy: MetaAdUserRef | null;
};

// E1 — lighter projection for the drafts list. Strips the heavy campaign/adSet/ad
// composition; the client joins accountName from its own accounts query (surveys
// precedent). "Archived" is DERIVED from deletedAt (no 'archived' status value);
// lastPublishError surfaces the failed-publish indicator.
export type MetaAdDraftSummary = {
  id: string;
  title: string;
  status: MetaAdDraftStatus;
  accountId: string;
  createdAt: IsoTimestamp;
  createdBy: MetaAdUserRef;
  updatedAt: IsoTimestamp;
  updatedBy: MetaAdUserRef | null;
  publishedAt: IsoTimestamp | null;
  publishedMetaAdId: string | null;
  deletedAt: IsoTimestamp | null; // non-null ⇒ archived
  lastPublishError: string | null; // PublishError.message when a prior publish failed
  // "Add an ad to a live ad set" drafts: the ad set the ad was added to (list label). null otherwise.
  attachedAdSetName?: string | null;
};

// Mutable subset accepted on create / draft-patch. id, status, version, snapshot,
// published* fields, lifecycle stamps + audit are server-owned.
export type MetaAdDraftInput = Pick<
  MetaAdDraft,
  'title' | 'description' | 'accountId' | 'campaign' | 'adSets' | 'ads' | 'driveSource' | 'attachTarget' | 'clonedFrom'
>;
